import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alan, Buton, OnayKutusu, Rozet, Secim } from "@koclukpanel/ui";
import { GOREV_RENGI_ETIKETI } from "@koclukpanel/contracts";
import {
  ApiHatasi,
  gorevEkle,
  gorevSonucunuDuzelt,
  gorevTasi,
  gorevYapamadiDuzelt,
  type ProgramGorevi,
} from "./api";
import { NEDENLER } from "../../ogrenci/src/Yapamadim";

const KOMPAKT_ANAHTARI = "program-kompakt";

/** A8 — depolama kapalı/dolu olduğunda görünüm anahtarı yine çalışır. */
export function useKompaktProgram() {
  const [kompakt, setKompakt] = useState(() => {
    try {
      return localStorage.getItem(KOMPAKT_ANAHTARI) === "true";
    } catch {
      return false;
    }
  });
  const degistir = () => {
    const yeni = !kompakt;
    setKompakt(yeni);
    try {
      localStorage.setItem(KOMPAKT_ANAHTARI, String(yeni));
    } catch {
      /* Tercih bu oturumda korunur. */
    }
  };
  return { kompakt, degistir };
}

/** F-STV-02a / A7 — null ölçüm sıfır değildir; girilmiş sıfır korunur. */
export function OgrenciGorevVerisi({ gorev }: { gorev: ProgramGorevi }) {
  const alan = (deger: number | null | undefined) =>
    deger == null ? "veri girilmedi" : String(deger);
  const sure = gorev.durationSeconds;
  return (
    <p className="text-notr-700 text-xs tabular-nums">
      D: {alan(gorev.correctCount)} · Y: {alan(gorev.wrongCount)} · B: {alan(gorev.emptyCount)} ·
      Süre: {sure == null ? "veri girilmedi" : `${Math.floor(sure / 60)} dk ${sure % 60} sn`}
    </p>
  );
}

/*
  GÜN ÖZETİ KÜÇÜK: sürüm 9a'da özet tablonun gün hücresinde (rowspan) duruyor;
  üç tam rozet o dar hücreyi satırlardan uzun yapıyordu. Görünen: renkli nokta
  + sayı; tam cümle ("Hedefe ulaştı: 3") ekran okuyucuda ve fare ipucunda.
*/
const OZET = [
  ["YESIL", "bg-tamam-nokta"],
  ["SARI", "bg-bekliyor-nokta"],
  ["KIRMIZI", "bg-eksik-nokta"],
] as const;

export function GunRenkOzeti({ gorevler }: { gorevler: readonly ProgramGorevi[] }) {
  return (
    <div aria-label="Günün renk özeti" className="flex flex-wrap justify-center gap-2">
      {OZET.map(([renk, nokta]) => {
        const metin = `${GOREV_RENGI_ETIKETI[renk]}: ${gorevler.filter((g) => g.renk === renk).length}`;
        return (
          <span
            key={renk}
            title={metin}
            className="text-metin-koyu inline-flex items-center gap-1 text-xs font-semibold tabular-nums"
          >
            <span aria-hidden="true" className={`size-2 rounded-full ${nokta}`} />
            <span aria-hidden="true">{gorevler.filter((g) => g.renk === renk).length}</span>
            <span className="sr-only">{metin}</span>
          </span>
        );
      })}
    </div>
  );
}

/** A8 — sonuç alanlarını taşımadan mevcut ekleme + taşıma uçlarıyla altına yerleştirir. */
export async function goreviAltinaKopyala(
  programId: string,
  gorev: ProgramGorevi,
  gunGorevleri: readonly ProgramGorevi[],
) {
  const kopya = await gorevEkle(programId, {
    itemDate: gorev.itemDate.slice(0, 10),
    kind: gorev.kind,
    lessonId: gorev.lessonId,
    topicId: gorev.topicId,
    studentResourceBookId: gorev.studentResourceBookId,
    pageFrom: gorev.pageFrom,
    pageTo: gorev.pageTo,
    testFrom: gorev.testFrom,
    testTo: gorev.testTo,
    targetQuestionCount: gorev.targetQuestionCount,
    targetTestCount: gorev.targetTestCount,
    startTime: gorev.startTime,
    youtubeUrl: gorev.youtubeUrl,
    videoTitle: gorev.videoTitle,
    topicPdfUrl: gorev.topicPdfUrl,
    testPdfUrl: gorev.testPdfUrl,
    coachNote: gorev.coachNote,
  });
  const sirali = [...gunGorevleri].sort((a, b) => a.sortOrder - b.sortOrder);
  const sira = sirali.findIndex((g) => g.id === gorev.id);
  sirali.splice(sira + 1, 0, kopya);
  try {
    // Boşluklu/eşit eski sıra numaraları da tek bir sıraya dönüşür.
    for (const [i, g] of sirali.entries()) {
      if (g.sortOrder !== i) await gorevTasi(g.id, gorev.itemDate.slice(0, 10), i);
    }
  } catch {
    throw new Error(
      "Kopya oluşturuldu; sıralama tamamlanamadı. Yenilenen listeden kopyayı taşıyabilirsiniz.",
    );
  }
}

/**
 * Görev satırındaki damgalar — renk rozetiyle aynı dil.
 * "Sonradan girildi" (F-STV-04a): öğrenci günü geçtikten sonra girdi; API zaten döndürüyordu,
 * panel göstermiyordu (UI/UX kanıtı 05.10.2026). "Koç düzeltti": sonuç öğrencinin değil koçun.
 */
export function GorevDamgalari({ gorev }: { gorev: ProgramGorevi }) {
  if (!gorev.lateEnteredAt && !gorev.editedByCoachAt) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {gorev.lateEnteredAt && (
        <Rozet ton="bilgi" isaret="↻">
          Sonradan girildi
        </Rozet>
      )}
      {gorev.editedByCoachAt && (
        <Rozet ton="notr" isaret="✎">
          Koç düzeltti
        </Rozet>
      )}
    </span>
  );
}

const sayiYaDaBos = (v: string): number | null => (v.trim() === "" ? null : Number(v));

/**
 * KOÇUN SONUÇ DÜZELTMESİ — A10 kararı ("hafta kapanınca öğrenci yazamaz, yalnız koç düzeltir").
 * Kural arayüzde uygulanamıyordu: panelde sonuç düzenleme ekranı yoktu (yardım ajanı ölçümü).
 * Öğrencinin kullandığı AYNI uçlar; yetki, kapsam (yalnız kendi öğrencisi — başka koçun öğrencisi
 * 403) ve doğrulama SUNUCUDA. Düzeltme `editedByCoachAt` ile damgalanır ve öğrencinin sonraki
 * yazımı onu ezemez (409 KOC_DUZELTTI).
 */
export function SonucDuzeltFormu({
  gorev,
  onBitti,
  onVazgec,
}: {
  gorev: ProgramGorevi;
  onBitti: () => void;
  onVazgec: () => void;
}) {
  const [tur, setTur] = useState<"SONUC" | "YAPAMADI">(gorev.missReason ? "YAPAMADI" : "SONUC");
  const [dogru, setDogru] = useState(gorev.correctCount?.toString() ?? "");
  const [yanlis, setYanlis] = useState(gorev.wrongCount?.toString() ?? "");
  const [bos, setBos] = useState(gorev.emptyCount?.toString() ?? "");
  const [dakika, setDakika] = useState(
    gorev.durationSeconds == null ? "" : String(Math.round(gorev.durationSeconds / 60)),
  );
  const [tamamlandi, setTamamlandi] = useState(gorev.status === "COMPLETED");
  const [neden, setNeden] = useState<string>(gorev.missReason ?? "NO_TIME");
  const [not, setNot] = useState(gorev.missNote ?? "");

  const kaydet = useMutation({
    mutationFn: () =>
      tur === "SONUC"
        ? gorevSonucunuDuzelt(gorev.id, {
            correctCount: sayiYaDaBos(dogru),
            wrongCount: sayiYaDaBos(yanlis),
            emptyCount: sayiYaDaBos(bos),
            durationSeconds: dakika.trim() === "" ? null : Math.round(Number(dakika) * 60),
            tamamlandi,
          })
        : gorevYapamadiDuzelt(gorev.id, neden, not.trim() || null),
    onSuccess: onBitti,
  });

  const gonder = (e: FormEvent) => {
    e.preventDefault();
    kaydet.mutate();
  };

  return (
    <form
      onSubmit={gonder}
      aria-label="Sonucu düzelt"
      className="rounded-alan border-notr-200 bg-notr-50 mt-1.5 flex w-full flex-col gap-2 border p-2"
    >
      <Secim
        etiket="Düzeltme"
        value={tur}
        onChange={(e) => setTur(e.target.value as "SONUC" | "YAPAMADI")}
      >
        <option value="SONUC">Sonuç (D/Y/B, süre)</option>
        <option value="YAPAMADI">Yapamadı (neden)</option>
      </Secim>
      {tur === "SONUC" ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Alan
              etiket="D"
              type="number"
              min={0}
              value={dogru}
              onChange={(e) => setDogru(e.target.value)}
            />
            <Alan
              etiket="Y"
              type="number"
              min={0}
              value={yanlis}
              onChange={(e) => setYanlis(e.target.value)}
            />
            <Alan
              etiket="B"
              type="number"
              min={0}
              value={bos}
              onChange={(e) => setBos(e.target.value)}
            />
          </div>
          <Alan
            etiket="Süre (dakika)"
            type="number"
            min={0}
            value={dakika}
            onChange={(e) => setDakika(e.target.value)}
          />
          <OnayKutusu
            etiket="Tamamlandı"
            checked={tamamlandi}
            onChange={(e) => setTamamlandi(e.target.checked)}
          />
        </>
      ) : (
        <>
          <Secim etiket="Neden" value={neden} onChange={(e) => setNeden(e.target.value)}>
            {NEDENLER.map((n) => (
              <option key={n.kod} value={n.kod}>
                {n.etiket}
              </option>
            ))}
          </Secim>
          <Alan etiket="Not" value={not} onChange={(e) => setNot(e.target.value)} />
        </>
      )}
      {kaydet.isError && (
        <p role="alert" className="text-hata-700 text-xs">
          {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
        </p>
      )}
      <div className="flex gap-2">
        <Buton type="submit" disabled={kaydet.isPending}>
          Kaydet
        </Buton>
        <Buton type="button" gorunum="sessiz" onClick={onVazgec}>
          Vazgeç
        </Buton>
      </div>
    </form>
  );
}
